import React from 'react'
import { Badge, Dropdown } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { useKeyboardInset } from 'shared/hooks/useKeyboardInset'
import { MenuCountBadge } from '../MenuCountBadge/MenuCountBadge'
import styles from './MobileTabBar.module.scss'

export interface IMobileTabChild {
  key: string
  label: string
  badgeCount?: number
}

export interface IMobileTab {
  key: string
  label: string
  icon: React.ReactNode
  badgeCount?: number
  // разделы вкладки: тап открывает их списком — тот же приём, что «↓» в меню на десктопе
  children?: IMobileTabChild[]
  isActive?: (pathname: string) => boolean
}

interface IMobileTabBarProps {
  tabs: IMobileTab[]
}

export const MobileTabBar: React.FC<IMobileTabBarProps> = ({ tabs }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const containerRef = React.useRef<HTMLDivElement>(null)

  useKeyboardInset(containerRef)

  const getActiveChild = (tab: IMobileTab): IMobileTabChild | undefined =>
    tab.children?.find((child) => location.pathname.startsWith(child.key))

  const isActive = (tab: IMobileTab): boolean => {
    if (tab.isActive) {
      return tab.isActive(location.pathname)
    }
    if (tab.children) {
      return Boolean(getActiveChild(tab))
    }
    return location.pathname.startsWith(tab.key)
  }

  const renderTab = (tab: IMobileTab, onClick?: () => void) => (
    <button
      type='button'
      className={classNames(styles.tab, {
        [styles.active]: isActive(tab)
      })}
      onClick={onClick}
    >
      <span className={styles.iconWrapper}>
        {tab.badgeCount ? (
          <Badge count={tab.badgeCount} size='small' offset={[4, -2]}>
            <span className={styles.icon}>{tab.icon}</span>
          </Badge>
        ) : (
          <span className={styles.icon}>{tab.icon}</span>
        )}
      </span>
      <span className={styles.text}>{getActiveChild(tab)?.label ?? tab.label}</span>
    </button>
  )

  return (
    <>
      <div className={styles.blurBackdrop} aria-hidden='true' />
      <div className={styles.container} ref={containerRef}>
        {tabs.map((tab) =>
          tab.children ? (
            <Dropdown
              key={tab.key}
              trigger={['click']}
              placement='top'
              overlayClassName={styles.tabMenu}
              // без анимации: пока antd анимирует opacity поповера, тот становится backdrop
              // root для меню и блюр не работает — стекло появлялось рывком после открытия
              transitionName=''
              menu={{
                items: tab.children.map(({ key, label, badgeCount }) => ({
                  key,
                  label: badgeCount ? (
                    <MenuCountBadge count={badgeCount}>{label}</MenuCountBadge>
                  ) : label
                })),
                selectedKeys: [getActiveChild(tab)?.key ?? ''],
                onClick: ({ key }) => navigate(key)
              }}
            >
              {renderTab(tab)}
            </Dropdown>
          ) : (
            <React.Fragment key={tab.key}>
              {renderTab(tab, () => navigate(tab.key))}
            </React.Fragment>
          )
        )}
      </div>
    </>
  )
}
